import React from "react";
import { Ship, Plane, Truck, Package, Users, Check } from "lucide-react";
import { Input } from "../ui/input";
import SectionHeader from "./SectionHeader";

function ModesOfTransportation({
  toggleSection,
  formData,
  handleInputChange,
  expandedSections,
}) {
  return (
    <div>
      {" "}
      <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <SectionHeader
          icon={Ship}
          title="Modes of Transportation"
          color="bg-cyan-600"
          section="s1b"
          expandedSections={expandedSections}
          toggleSection={toggleSection}
        />
        {expandedSections.s1b && (
          <div className="p-4">
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-3">
              Select transportation modes this customer operates in. Each
              selected mode will create a dedicated configuration tab.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Ocean */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Ship className="w-4 h-4 text-blue-500" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Ocean
                  </span>
                </div>
                <div className="flex flex-col gap-2 ml-6">
                  <label
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all ${
                      !!formData?.OCEAN_IMPORT
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <Input
                      type="checkbox"
                      className="sr-only"
                      checked={!!formData?.OCEAN_IMPORT}
                      onChange={handleInputChange}
                      name="OCEAN_IMPORT"
                    />
                    {!!formData?.OCEAN_IMPORT && <Check className="w-3 h-3" />}
                    Import
                  </label>
                  <label
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all ${
                      !!formData?.OCEAN_EXPORT
                        ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-300 dark:border-blue-700"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <Input
                      type="checkbox"
                      className="sr-only"
                      checked={!!formData?.OCEAN_EXPORT}
                      onChange={handleInputChange}
                      name="OCEAN_EXPORT"
                    />
                    {!!formData?.OCEAN_EXPORT && <Check className="w-3 h-3" />}
                    Export
                  </label>
                </div>
              </div>

              {/* Air */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="flex items-center gap-2 mb-2">
                  <Plane className="w-4 h-4 text-purple-500" />
                  <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Air
                  </span>
                </div>
                <div className="flex flex-col gap-2 ml-6">
                  <label
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all ${
                      !!formData?.AIR_IMPORT
                        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <Input
                      type="checkbox"
                      className="sr-only"
                      checked={!!formData?.AIR_IMPORT}
                      onChange={handleInputChange}
                      name="AIR_IMPORT"
                    />
                    {!!formData?.AIR_IMPORT && <Check className="w-3 h-3" />}
                    Import
                  </label>
                  <label
                    className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium cursor-pointer transition-all ${
                      !!formData?.AIR_EXPORT
                        ? "bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border border-purple-300 dark:border-purple-700"
                        : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-600"
                    }`}
                  >
                    <Input
                      type="checkbox"
                      className="sr-only"
                      checked={!!formData?.AIR_EXPORT}
                      onChange={handleInputChange}
                      name="AIR_EXPORT"
                    />
                    {!!formData?.AIR_EXPORT && <Check className="w-3 h-3" />}
                    Export
                  </label>
                </div>
              </div>

              {/* Domestic/Trucking */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <label
                  className={`flex items-center gap-2 cursor-pointer ${
                    !!formData?.DOMESTIC_TOWING
                      ? "text-amber-700 dark:text-amber-400"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <Input
                    type="checkbox"
                    className="sr-only"
                    checked={!!formData?.DOMESTIC_TOWING}
                    onChange={handleInputChange}
                    name="DOMESTIC_TOWING"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      !!formData?.DOMESTIC_TOWING
                        ? "bg-amber-500 border-amber-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  >
                    {!!formData?.DOMESTIC_TOWING && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Truck className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-semibold">
                    Domestic / Trucking
                  </span>
                </label>
              </div>

              {/* Warehousing */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <label
                  className={`flex items-center gap-2 cursor-pointer ${
                    !!formData?.WAREHOUSING
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <Input
                    type="checkbox"
                    className="sr-only"
                    checked={!!formData?.WAREHOUSING}
                    onChange={handleInputChange}
                    name="WAREHOUSING"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      !!formData?.WAREHOUSING
                        ? "bg-emerald-500 border-emerald-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  >
                    {!!formData?.WAREHOUSING && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Package className="w-4 h-4 text-emerald-500" />
                  <span className="text-sm font-semibold">Warehousing</span>
                </label>
              </div>

              {/* Distribution */}
              <div className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700">
                <label
                  className={`flex items-center gap-2 cursor-pointer ${
                    !!formData?.DISTRIBUTION
                      ? "text-rose-700 dark:text-rose-400"
                      : "text-slate-700 dark:text-slate-300"
                  }`}
                >
                  <Input
                    type="checkbox"
                    className="sr-only"
                    checked={!!formData?.DISTRIBUTION}
                    onChange={handleInputChange}
                    name="DISTRIBUTION"
                  />
                  <div
                    className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                      !!formData?.DISTRIBUTION
                        ? "bg-rose-500 border-rose-500"
                        : "border-slate-300 dark:border-slate-600"
                    }`}
                  >
                    {!!formData?.DISTRIBUTION && (
                      <Check className="w-3 h-3 text-white" />
                    )}
                  </div>
                  <Users className="w-4 h-4 text-rose-500" />
                  <span className="text-sm font-semibold">Distribution</span>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ModesOfTransportation;
